import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import {
  Loader2, Plus, Link2, Trash2, BarChart3, CalendarPlus, ClipboardList, X, Library,
  Send, Users, FlaskConical,
} from 'lucide-react';

/**
 * SurveysEventsPanel — the org-wide half of OL BookMe (`OL_BOOKME.md` §6e).
 *
 *  * Surveys  — `/api/surveys`, questions pulled from the shared
 *               `reg_question_library` (no second question engine).
 *  * Events   — `/api/org-events`, stored in the existing `team_events`
 *               collection. Never `historical_games`: that is the games SOT.
 *
 * Both are organization-wide, not per sport, and say so.
 */
const Q_TYPES = [
  { v: 'text', l: 'Short text' },
  { v: 'long_text', l: 'Paragraph' },
  { v: 'dropdown', l: 'Dropdown' },
  { v: 'checkbox', l: 'Checkboxes' },
  { v: 'number', l: 'Number' },
  { v: 'date', l: 'Date' },
];
const CHOICE = ['dropdown', 'checkbox', 'multiple_choice', 'radio'];
const STATUS_STYLE = { draft: 'bg-slate-500/70', open: 'bg-emerald-600', closed: 'bg-slate-600' };

export default function SurveysEventsPanel({ orgId, isDark = true }) {
  const BASE = process.env.REACT_APP_BACKEND_URL;
  const [tab, setTab] = useState('surveys');

  const text = isDark ? 'text-white' : 'text-slate-900';
  const muted = isDark ? 'text-slate-400' : 'text-slate-500';
  const dim = isDark ? 'text-slate-500' : 'text-slate-400';
  const cardCls = isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200';
  const inputCls = isDark ? 'bg-slate-800 border-slate-600 text-white' : '';
  const selCls = `h-8 text-xs rounded-md px-2 border ${isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-slate-300'}`;

  // ── surveys ───────────────────────────────────────────────────
  const [surveys, setSurveys] = useState([]);
  const [library, setLibrary] = useState([]);
  const [sLoading, setSLoading] = useState(true);
  const [newSurvey, setNewSurvey] = useState(null);   // the draft being built
  const [results, setResults] = useState(null);
  const [dist, setDist] = useState(null);        // the survey being distributed
  const [audience, setAudience] = useState('families');
  const [preview, setPreview] = useState(null);
  const [testTo, setTestTo] = useState('');
  const [busy, setBusy] = useState('');

  const loadSurveys = useCallback(async () => {
    if (!orgId) return;
    setSLoading(true);
    try {
      const { data } = await axios.get(`${BASE}/api/surveys/${orgId}`, { withCredentials: true });
      setSurveys(data?.surveys || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load surveys.');
    } finally { setSLoading(false); }
  }, [orgId, BASE]);

  useEffect(() => { loadSurveys(); }, [loadSurveys]);

  const loadLibrary = async () => {
    try {
      const { data } = await axios.get(`${BASE}/api/reg-builder/${orgId}/question-library`,
        { withCredentials: true });
      setLibrary(data?.questions || data?.library || []);
    } catch { setLibrary([]); }
  };

  const createSurvey = async () => {
    const d = newSurvey;
    if (!d?.title?.trim()) { toast.error('Give the survey a title.'); return; }
    const questions = (d.questions || []).filter(q => q.label.trim());
    setBusy('survey');
    try {
      await axios.post(`${BASE}/api/surveys/${orgId}`, {
        title: d.title.trim(), description: d.description || '',
        anonymous: !!d.anonymous, questions,
      }, { withCredentials: true });
      setNewSurvey(null); await loadSurveys();
      toast.success('Survey created as a draft — open it when you are ready.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create the survey.');
    } finally { setBusy(''); }
  };

  const setStatus = async (s, status) => {
    setBusy(s.id);
    try {
      const { data } = await axios.post(`${BASE}/api/surveys/${orgId}/${s.id}/status`,
        { status }, { withCredentials: true });
      await loadSurveys();
      toast.success(status === 'open'
        ? 'Survey is open — share the link.' : `Survey ${status}.`);
      if (status === 'open' && data?.public_link) copy(data.public_link);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'That did not work.');
    } finally { setBusy(''); }
  };

  const showResults = async (s) => {
    try {
      const { data } = await axios.get(`${BASE}/api/surveys/${orgId}/${s.id}/results`,
        { withCredentials: true });
      setResults(data);
    } catch { toast.error('Could not load results.'); }
  };

  const deleteSurvey = async (s) => {
    if (!window.confirm(`Delete “${s.title}” and all of its responses?`)) return;
    try {
      const { data } = await axios.delete(`${BASE}/api/surveys/${orgId}/${s.id}`,
        { withCredentials: true });
      await loadSurveys();
      toast.success(`Deleted — ${data?.responses_deleted || 0} response(s) removed.`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed.'); }
  };


  const openDistribute = async (s) => {
    setDist(s); setPreview(null); setAudience('families'); setTestTo('');
    loadPreview(s, 'families');
  };
  const loadPreview = async (s, aud) => {
    try {
      const { data } = await axios.get(`${BASE}/api/surveys/${orgId}/${s.id}/audience`,
        { params: { audience: aud }, withCredentials: true });
      setPreview(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load the audience.'); }
  };
  const sendTest = async () => {
    if (!testTo.trim()) { toast.error('Enter an address for the test.'); return; }
    setBusy('dist');
    try {
      await axios.post(`${BASE}/api/surveys/${orgId}/${dist.id}/distribute`,
        { test_email: testTo.trim() }, { withCredentials: true });
      toast.success(`Test sent to ${testTo.trim()} — nothing was recorded.`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Test failed.'); }
    finally { setBusy(''); }
  };
  const sendReal = async (resend) => {
    const n = preview?.count || 0;
    if (!window.confirm(
      `Send “${dist.title}” to ${n} recipient(s)?

This sends real email and cannot be undone.`)) return;
    setBusy('dist');
    try {
      const { data } = await axios.post(`${BASE}/api/surveys/${orgId}/${dist.id}/distribute`,
        { audience, ...(resend ? { resend: true } : {}) }, { withCredentials: true });
      toast.success(`Sent to ${data.sent} recipient(s)` +
        (data.failed_count ? ` · ${data.failed_count} failed` : '') +
        (data.skipped_already_invited ? ` · ${data.skipped_already_invited} already had it` : ''));
      setDist(null); await loadSurveys();
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed.'); }
    finally { setBusy(''); }
  };

  const copy = (link) => {
    try { navigator.clipboard.writeText(link); toast.success('Link copied.'); }
    catch { toast.error('Could not copy — copy it manually.'); }
  };

  // ── events ────────────────────────────────────────────────────
  const [events, setEvents] = useState([]);
  const [eLoading, setELoading] = useState(true);
  const [newEvent, setNewEvent] = useState(null);
  const [types, setTypes] = useState([]);

  const loadEvents = useCallback(async () => {
    if (!orgId) return;
    setELoading(true);
    try {
      const { data } = await axios.get(`${BASE}/api/org-events/${orgId}`, { withCredentials: true });
      setEvents(data?.events || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load events.');
    } finally { setELoading(false); }
  }, [orgId, BASE]);

  useEffect(() => { loadEvents(); }, [loadEvents]);
  useEffect(() => {
    axios.get(`${BASE}/api/org-events/types`, { withCredentials: true })
      .then(({ data }) => setTypes(data?.suggested || [])).catch(() => {});
  }, [BASE]);

  const createEvent = async () => {
    const d = newEvent;
    if (!d?.title?.trim() || !d?.date) { toast.error('Title and date are required.'); return; }
    setBusy('event');
    try {
      await axios.post(`${BASE}/api/org-events/${orgId}`, {
        title: d.title.trim(), date: d.date, event_type: d.event_type || 'other',
        start_time: d.start_time || null, location: d.location || '', notes: d.notes || '',
      }, { withCredentials: true });
      setNewEvent(null); await loadEvents();
      toast.success('Event added.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add the event.');
    } finally { setBusy(''); }
  };

  const deleteEvent = async (ev) => {
    if (!window.confirm(`Delete “${ev.title}”?`)) return;
    try {
      await axios.delete(`${BASE}/api/org-events/${orgId}/${ev.id}`, { withCredentials: true });
      await loadEvents(); toast.success('Event deleted.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed.'); }
  };

  const tabBtn = (v, label, Icon) => (
    <Button size="sm" variant={tab === v ? 'default' : 'ghost'}
      className={tab === v ? 'bg-slate-600 hover:bg-slate-700 text-white h-7 text-xs'
                           : `${muted} h-7 text-xs`}
      onClick={() => setTab(v)} data-testid={`se-tab-${v}`}>
      <Icon className="h-3 w-3 mr-1" />{label}
    </Button>
  );

  return (
    <div className="space-y-3" data-testid="surveys-events-panel">
      <div className="flex items-center gap-1 flex-wrap">
        {tabBtn('surveys', 'Surveys', ClipboardList)}
        {tabBtn('events', 'Org Events', CalendarPlus)}
        <span className={`text-[11px] ml-auto ${dim}`}>Organization-wide — not per sport.</span>
      </div>

      {/* ── SURVEYS ── */}
      {tab === 'surveys' && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Button size="sm" className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => { setNewSurvey({ title: '', description: '', anonymous: false, questions: [] }); loadLibrary(); }}
              data-testid="survey-new">
              <Plus className="h-3 w-3 mr-1" />New survey
            </Button>
            {sLoading && <Loader2 className={`h-3 w-3 animate-spin ${dim}`} />}
          </div>

          {!sLoading && surveys.length === 0 && (
            <Card className={cardCls}><CardContent className="p-8 text-center">
              <p className={`text-sm ${muted}`}>No surveys yet.</p>
              <p className={`text-xs mt-1 ${dim}`}>
                Build a question set, open it, and share the link. Questions can come from your
                existing registration question library.
              </p>
            </CardContent></Card>
          )}

          {surveys.map(s => (
            <Card key={s.id} className={cardCls} data-testid={`survey-row-${s.id}`}>
              <CardContent className="p-3 flex items-center gap-2 flex-wrap">
                <div>
                  <span className={`text-sm font-semibold ${text}`}>{s.title}</span>
                  <span className={`block text-[11px] ${dim}`}>
                    {(s.questions || []).length} question(s) · {s.response_count || 0} response(s)
                    {s.invites_sent ? ` · sent to ${s.invites_sent}` : ''}
                    {s.anonymous ? ' · anonymous' : ''}
                  </span>
                </div>
                <div className="flex items-center gap-2 ml-auto">
                  <Badge className={`${STATUS_STYLE[s.status] || 'bg-slate-500'} text-[10px]`}>{s.status}</Badge>
                  {s.status !== 'open' && (
                    <Button size="sm" variant="outline" disabled={busy === s.id}
                      className={`h-7 text-xs ${isDark ? 'border-slate-600 text-slate-300' : ''}`}
                      onClick={() => setStatus(s, 'open')} data-testid={`survey-open-${s.id}`}>Open</Button>
                  )}
                  {s.status === 'open' && (
                    <>
                      <Button size="sm" variant="outline"
                        className={`h-7 text-xs ${isDark ? 'border-slate-600 text-slate-300' : ''}`}
                        onClick={() => copy(`${window.location.origin}/survey/${s.public_token}`)}
                        data-testid={`survey-link-${s.id}`}>
                        <Link2 className="h-3 w-3 mr-1" />Link
                      </Button>
                      <Button size="sm" variant="outline"
                        className={`h-7 text-xs ${isDark ? 'border-slate-600 text-slate-300' : ''}`}
                        onClick={() => openDistribute(s)} data-testid={`survey-send-${s.id}`}>
                        <Send className="h-3 w-3 mr-1" />Send
                      </Button>
                      <Button size="sm" variant="outline" disabled={busy === s.id}
                        className={`h-7 text-xs ${isDark ? 'border-slate-600 text-slate-300' : ''}`}
                        onClick={() => setStatus(s, 'closed')} data-testid={`survey-close-${s.id}`}>Close</Button>
                    </>
                  )}
                  <Button size="sm" variant="ghost" className={`h-7 text-xs ${muted}`}
                    onClick={() => showResults(s)} data-testid={`survey-results-${s.id}`}>
                    <BarChart3 className="h-3 w-3" />
                  </Button>
                  <Button size="sm" variant="ghost" className={`h-7 text-xs ${muted}`}
                    onClick={() => deleteSurvey(s)} data-testid={`survey-delete-${s.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ── EVENTS ── */}
      {tab === 'events' && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Button size="sm" className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => setNewEvent({ title: '', date: '', event_type: 'other', location: '' })}
              data-testid="event-new">
              <Plus className="h-3 w-3 mr-1" />Add event
            </Button>
            {eLoading && <Loader2 className={`h-3 w-3 animate-spin ${dim}`} />}
            <span className={`text-[11px] ml-auto ${dim}`}>
              Banquets, rallies, picture day — not games.
            </span>
          </div>

          {!eLoading && events.length === 0 && (
            <Card className={cardCls}><CardContent className="p-8 text-center">
              <p className={`text-sm ${muted}`}>No org events yet.</p>
            </CardContent></Card>
          )}

          {events.map(ev => (
            <Card key={ev.id} className={cardCls} data-testid={`event-row-${ev.id}`}>
              <CardContent className="p-3 flex items-center gap-2 flex-wrap text-xs">
                <span className={`font-medium ${text}`}>{ev.date}</span>
                {ev.start_time && <span className={muted}>{ev.start_time}</span>}
                <span className={text}>{ev.title}</span>
                <Badge variant="outline" className="text-[9px]">{ev.event_type}</Badge>
                {ev.location && <span className={muted}>{ev.location}</span>}
                <Button size="sm" variant="ghost" className={`h-7 ml-auto ${muted}`}
                  onClick={() => deleteEvent(ev)} data-testid={`event-delete-${ev.id}`}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ── new survey ── */}
      <Dialog open={!!newSurvey} onOpenChange={o => !o && setNewSurvey(null)}>
        <DialogContent className={`max-w-2xl ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white'}`}>
          <DialogHeader><DialogTitle className={text}>New survey</DialogTitle></DialogHeader>
          {newSurvey && (
            <div className="space-y-3 py-1 max-h-[70vh] overflow-y-auto" data-testid="survey-dialog">
              <div>
                <Label className={`text-xs ${muted}`}>Title</Label>
                <Input value={newSurvey.title} className={inputCls}
                  onChange={e => setNewSurvey(s => ({ ...s, title: e.target.value }))}
                  placeholder="e.g. Uniform sizes for next season" data-testid="survey-title" />
              </div>
              <div>
                <Label className={`text-xs ${muted}`}>Description (optional)</Label>
                <Input value={newSurvey.description} className={inputCls}
                  onChange={e => setNewSurvey(s => ({ ...s, description: e.target.value }))} />
              </div>
              <label className={`flex items-center gap-2 text-xs ${text} cursor-pointer`}>
                <input type="checkbox" checked={newSurvey.anonymous}
                  onChange={e => setNewSurvey(s => ({ ...s, anonymous: e.target.checked }))}
                  data-testid="survey-anonymous" />
                Anonymous — store no respondent details at all
              </label>

              <div className={`border-t pt-2 ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-xs font-semibold ${text}`}>Questions</span>
                  <Button size="sm" variant="outline" className={`h-6 text-[11px] ml-auto ${isDark ? 'border-slate-600 text-slate-300' : ''}`}
                    onClick={() => setNewSurvey(s => ({ ...s, questions: [...s.questions, { id: `n${Date.now()}`, label: '', qtype: 'text', options: [], required: false }] }))}
                    data-testid="survey-add-question">
                    <Plus className="h-3 w-3 mr-1" />Add question
                  </Button>
                </div>

                {library.length > 0 && (
                  <div className={`text-[11px] mb-2 ${dim}`}>
                    <Library className="h-3 w-3 inline mr-1" />
                    From your question library:{' '}
                    {library.slice(0, 8).map(q => (
                      <button key={q.id} className={`underline mr-2 ${muted}`}
                        onClick={() => setNewSurvey(s => ({ ...s, questions: [...s.questions, {
                          id: `n${Date.now()}${Math.random()}`, label: q.label, qtype: q.qtype || 'text',
                          options: q.options || [], required: !!q.required, library_id: q.id }] }))}
                        data-testid={`survey-lib-${q.id}`}>+ {q.label}</button>
                    ))}
                  </div>
                )}

                {newSurvey.questions.map((q, i) => (
                  <div key={q.id} className={`flex items-start gap-2 mb-2 p-2 rounded border ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                    <div className="flex-1 space-y-1">
                      <Input value={q.label} className={`${inputCls} h-8`} placeholder="Question"
                        onChange={e => setNewSurvey(s => { const qs=[...s.questions]; qs[i]={...qs[i],label:e.target.value}; return {...s,questions:qs}; })}
                        data-testid={`survey-q-label-${i}`} />
                      {CHOICE.includes(q.qtype) && (
                        <Input value={(q.options || []).join(', ')} className={`${inputCls} h-8`}
                          placeholder="Options, comma separated"
                          onChange={e => setNewSurvey(s => { const qs=[...s.questions]; qs[i]={...qs[i],options:e.target.value.split(',').map(x=>x.trim()).filter(Boolean)}; return {...s,questions:qs}; })}
                          data-testid={`survey-q-options-${i}`} />
                      )}
                    </div>
                    <select className={selCls} value={q.qtype}
                      onChange={e => setNewSurvey(s => { const qs=[...s.questions]; qs[i]={...qs[i],qtype:e.target.value}; return {...s,questions:qs}; })}
                      data-testid={`survey-q-type-${i}`}>
                      {Q_TYPES.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
                    </select>
                    <label className={`flex items-center gap-1 text-[11px] ${muted}`}>
                      <input type="checkbox" checked={q.required}
                        onChange={e => setNewSurvey(s => { const qs=[...s.questions]; qs[i]={...qs[i],required:e.target.checked}; return {...s,questions:qs}; })} />
                      req
                    </label>
                    <button className={muted}
                      onClick={() => setNewSurvey(s => ({ ...s, questions: s.questions.filter((_, j) => j !== i) }))}>
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>

              <p className={`text-[11px] ${dim}`}>
                Questions cannot change once responses arrive — answers are keyed to them.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" className={muted} onClick={() => setNewSurvey(null)}>Cancel</Button>
                <Button onClick={createSurvey} disabled={busy === 'survey'}
                  className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="survey-create">
                  {busy === 'survey' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                  Create as draft
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── new event ── */}
      <Dialog open={!!newEvent} onOpenChange={o => !o && setNewEvent(null)}>
        <DialogContent className={`max-w-md ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white'}`}>
          <DialogHeader><DialogTitle className={text}>Add an org event</DialogTitle></DialogHeader>
          {newEvent && (
            <div className="space-y-3 py-1" data-testid="event-dialog">
              <div>
                <Label className={`text-xs ${muted}`}>Title</Label>
                <Input value={newEvent.title} className={inputCls} placeholder="e.g. Sports Banquet"
                  onChange={e => setNewEvent(v => ({ ...v, title: e.target.value }))}
                  data-testid="event-title" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className={`text-xs ${muted}`}>Date</Label>
                  <Input type="date" value={newEvent.date} className={inputCls}
                    onChange={e => setNewEvent(v => ({ ...v, date: e.target.value }))}
                    data-testid="event-date" />
                </div>
                <div>
                  <Label className={`text-xs ${muted}`}>Time</Label>
                  <Input type="time" value={newEvent.start_time || ''} className={inputCls}
                    onChange={e => setNewEvent(v => ({ ...v, start_time: e.target.value }))}
                    data-testid="event-time" />
                </div>
                <div>
                  <Label className={`text-xs ${muted}`}>Type</Label>
                  <select className={`${selCls} w-full`} value={newEvent.event_type}
                    onChange={e => setNewEvent(v => ({ ...v, event_type: e.target.value }))}
                    data-testid="event-type">
                    {(types.length ? types : ['other']).map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label className={`text-xs ${muted}`}>Location (optional)</Label>
                <Input value={newEvent.location} className={inputCls}
                  onChange={e => setNewEvent(v => ({ ...v, location: e.target.value }))}
                  data-testid="event-location" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" className={muted} onClick={() => setNewEvent(null)}>Cancel</Button>
                <Button onClick={createEvent} disabled={busy === 'event'}
                  className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="event-create">
                  {busy === 'event' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                  Add event
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── distribute ── */}
      <Dialog open={!!dist} onOpenChange={o => !o && setDist(null)}>
        <DialogContent className={`max-w-lg ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white'}`}>
          <DialogHeader><DialogTitle className={text}>Send “{dist?.title}”</DialogTitle></DialogHeader>
          {dist && (
            <div className="space-y-3 py-1" data-testid="survey-distribute-dialog">
              <div>
                <Label className={`text-xs ${muted}`}>Audience</Label>
                <select className={`${selCls} w-full`} value={audience}
                  onChange={e => { setAudience(e.target.value); loadPreview(dist, e.target.value); }}
                  data-testid="dist-audience">
                  <option value="families">Families</option>
                  <option value="staff">Staff</option>
                  <option value="both">Families and staff</option>
                </select>
              </div>

              {preview && (
                <div className={`rounded-md border px-3 py-2 text-xs ${isDark ? 'border-slate-700 bg-slate-800/40' : 'border-slate-200 bg-slate-50'}`}
                  data-testid="dist-preview">
                  <div className="flex items-center gap-2">
                    <Users className="h-3 w-3 text-amber-500" />
                    <span className={text}><strong>{preview.count}</strong> recipient(s)</span>
                    <span className={muted}>
                      {preview.families} famil{preview.families === 1 ? 'y' : 'ies'} · {preview.staff} staff
                    </span>
                  </div>
                  {preview.sample?.length > 0 && (
                    <div className={`mt-1 ${dim}`}>e.g. {preview.sample.join(', ')}…</div>
                  )}
                  {preview.already_sent > 0 && (
                    <div className="mt-1 text-amber-500">
                      Already sent to {preview.already_sent}. Sending again only reaches those who
                      have not had it.
                    </div>
                  )}
                </div>
              )}

              <div className={`rounded-md border px-3 py-2 text-[11px] ${isDark
                  ? 'border-amber-700/50 bg-amber-900/15 text-amber-300'
                  : 'border-amber-300 bg-amber-50 text-amber-800'}`}>
                This sends <strong>real email</strong> and cannot be undone. Send a test to yourself
                first. Each person gets their own link, so you can see who has answered
                {dist.anonymous ? ' — answers stay anonymous' : ''}.
              </div>

              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label className={`text-xs ${muted}`}>Test to</Label>
                  <Input value={testTo} className={`${inputCls} h-8`} type="email"
                    placeholder="you@yourclub.org"
                    onChange={e => setTestTo(e.target.value)} data-testid="dist-test-email" />
                </div>
                <Button size="sm" variant="outline" disabled={busy === 'dist' || !testTo.trim()}
                  className={`h-8 text-xs ${isDark ? 'border-slate-600 text-slate-300' : ''}`}
                  onClick={sendTest} data-testid="dist-test-send">
                  <FlaskConical className="h-3 w-3 mr-1" />Send test
                </Button>
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <Button variant="ghost" className={muted} onClick={() => setDist(null)}>Cancel</Button>
                {preview?.already_sent > 0 && (
                  <Button variant="outline" disabled={busy === 'dist'}
                    className={`${isDark ? 'border-slate-600 text-slate-300' : ''}`}
                    onClick={() => sendReal(true)} data-testid="dist-resend">
                    Send to the rest
                  </Button>
                )}
                <Button onClick={() => sendReal(false)}
                  disabled={busy === 'dist' || !preview?.count || preview?.already_sent > 0}
                  className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="dist-send">
                  {busy === 'dist' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                  Send to {preview?.count || 0}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── results ── */}
      <Dialog open={!!results} onOpenChange={o => !o && setResults(null)}>
        <DialogContent className={`max-w-xl ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white'}`}>
          <DialogHeader><DialogTitle className={text}>{results?.title} — results</DialogTitle></DialogHeader>
          {results && (
            <div className="space-y-3 py-1 max-h-[65vh] overflow-y-auto" data-testid="survey-results">
              <div className={`text-xs ${muted}`}>
                {results.responses} response(s){results.anonymous ? ' · anonymous' : ''}
                {results.invited ? ` · ${results.responded}/${results.invited} invited answered (${results.response_rate}%)` : ''}
              </div>
              {(results.questions || []).map(q => (
                <div key={q.id} className={`border-t pt-2 ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                  <div className={`text-xs font-semibold ${text}`}>{q.label}</div>
                  <div className={`text-[11px] ${dim} mb-1`}>{q.answered} answered</div>
                  {q.counts ? Object.entries(q.counts).map(([k, n]) => (
                    <div key={k} className="flex items-center gap-2 text-[11px]">
                      <span className={`w-40 truncate ${muted}`}>{k}</span>
                      <div className="flex-1 h-2 rounded bg-slate-700/40">
                        <div className="h-2 rounded bg-amber-600"
                          style={{ width: `${Math.round((n / Math.max(1, results.responses)) * 100)}%` }} />
                      </div>
                      <span className={text}>{n}</span>
                    </div>
                  )) : (
                    <div className={`text-[11px] ${muted} space-y-0.5`}>
                      {(q.answers || []).slice(0, 25).map((a, i) => <div key={i}>• {a}</div>)}
                      {(q.answers || []).length === 0 && <div className={dim}>No answers yet.</div>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
